import React, { useRef, useState } from "react";
import styled, { css } from "styled-components";

import Props from "./props";
import useOnClickOutside from "../../../../hooks/useOnClickOutside";

interface OptionProps {
    selected: boolean;
    isSubOption?: boolean;
}

const Select: React.FC<Props> = ({ options, value, placeholder, closeOnSelect, getOptionText, setOptionKey, onOptionClick, isSelectedOption }) => {
    const ref = useRef(null);
    let selectedOptionText = placeholder;
    if (value) {
        switch (value.constructor) {
            case Array:
                if (value.length) {
                    selectedOptionText = value.map((item: any) => getOptionText(item)).join(", ");
                }
                break;
            case Object:
                if (getOptionText(value)) {
                    selectedOptionText = getOptionText(value);
                }
                break;
            case String:
                if (value) {
                    selectedOptionText = value;
                }
                break;
            default:
                selectedOptionText = placeholder;
        }
    }
    const [open, toggleOpen] = useState(false);
    useOnClickOutside(ref, () => {
        if (open) {
            toggleOpen(false);
        }
    });
    return (
        <Wrapper>
            <Value onClick={() => options.length && toggleOpen(!open)}>{selectedOptionText}</Value>
            {open && (
                <Selector ref={ref}>
                    {options.map(option => {
                        const isOptionSelected = isSelectedOption(option);
                        const { subCategories } = option;
                        return (
                            <React.Fragment key={setOptionKey(option)}>
                                <Option
                                    onClick={() => {
                                        if (closeOnSelect) {
                                            toggleOpen(false);
                                        }
                                        onOptionClick(option);
                                    }}
                                    selected={isOptionSelected}
                                >
                                    {getOptionText(option)}
                                </Option>
                                {subCategories && subCategories.length
                                    ? subCategories.map((subOption: any) => {
                                          const isSuboptionSelected = isSelectedOption(subOption);
                                          return (
                                              <React.Fragment key={setOptionKey(subOption)}>
                                                  <Option
                                                      onClick={() => {
                                                          if (closeOnSelect) {
                                                              toggleOpen(false);
                                                          }
                                                          onOptionClick(subOption);
                                                      }}
                                                      selected={isSuboptionSelected}
                                                      isSubOption
                                                  >
                                                      {getOptionText(subOption)}
                                                  </Option>
                                              </React.Fragment>
                                          );
                                      })
                                    : null}
                            </React.Fragment>
                        );
                    })}
                </Selector>
            )}
        </Wrapper>
    );
};

export default Select;

const Wrapper = styled.div`
    position: relative;
`;

const Value = styled.div`
    position: relative;
    box-shadow: 0 0 0.2rem ${props => props.theme.colors.primary(1)};
    border-radius: 0.3rem;
    padding: 0.5rem;
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-right: 1.7rem;
    &:before {
        content: "";
        width: 0;
        height: 0;
        border-left: 0.4rem solid transparent;
        border-right: 0.4rem solid transparent;
        border-top: 0.4rem solid ${props => props.theme.colors.primary(1)};
        position: absolute;
        right: 0.5rem;
        top: 50%;
        transform: translateY(-50%);
    }
`;

const Selector = styled.div`
    position: absolute;
    user-select: none;
    left: -0.2rem;
    right: -0.2rem;
    top: -0.2rem;
    background-color: ${props => props.theme.colors.primaryInvert(1)};
    box-shadow: 0 0 0.2rem ${props => props.theme.colors.primary(1)};
    border-radius: 0.3rem;
    overflow: auto;
    padding: 0.3rem 0;
    max-height: 15rem;
`;

const Option = styled.div<OptionProps>`
    position: relative;
    cursor: pointer;
    padding: 0.5rem 1rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: background-color 0.3s ease;
    &:hover {
        background-color: ${props => props.theme.colors.primary(0.8)};
        color: ${props => props.theme.colors.primaryInvert(0.8)};
    }
    ${({ selected }) =>
        selected &&
        css`
            background-color: ${props => props.theme.colors.primary(1)};
            color: ${props => props.theme.colors.primaryInvert(1)};
            &:hover {
                background-color: ${props => props.theme.colors.primary(1)};
                color: ${props => props.theme.colors.primaryInvert(1)};
            }
        `}
    ${({ isSubOption }) =>
        isSubOption &&
        css`
            padding-left: 2rem;
        `}
`;
